import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag, ArrowLeft, ArrowRight, ChevronRight, Home } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour formater les URLs d'images
const getImageUrl = (img) => {
  if (!img) return '/placeholder-product.jpg';
  if (img.startsWith('/api/')) return `${API_URL}${img}`;
  if (img.startsWith('http')) return img;
  return img;
};

export function CartPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, loading, updateQuantity, removeItem } = useCart();
  const { formatPrice, currency } = useCurrency();
  const [promoCode, setPromoCode] = useState('');
  const [previousPage, setPreviousPage] = useState(null);
  const isRTL = i18n.dir() === 'rtl';

  // Récupérer la page précédente depuis l'historique ou le state
  useEffect(() => {
    // Vérifier si on a un referrer dans le state
    if (location.state?.from) {
      setPreviousPage(location.state.from);
    } else {
      // Essayer de récupérer depuis sessionStorage
      const lastProduct = sessionStorage.getItem('lastViewedProduct');
      if (lastProduct) {
        setPreviousPage(JSON.parse(lastProduct));
      }
    }
  }, [location]);

  const handleQuantityChange = (index, delta) => {
    const item = cart.items[index];
    const newQuantity = item.quantity + delta;
    if (newQuantity > 0) {
      updateQuantity(index, newQuantity);
    }
  };

  const handleRemove = (index) => {
    removeItem(index);
  };

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark" data-testid="cart-page">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Fil d'Ariane / Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">{t('nav.home', 'Accueil')}</span>
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-white font-medium">{t('cart.title', 'Panier')}</span>
        </nav>

        {/* Bouton retour vers la page précédente */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(-1)}
            className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
          >
            <ArrowIcon className="h-4 w-4 me-2" />
            {t('common.back', 'Retour')}
          </Button>
          <Link to="/products">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-mutagim-red hover:text-red-400 hover:bg-red-500/10"
            >
              {t('cart.continueShopping', 'Continuer mes achats')}
            </Button>
          </Link>
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8">
          {t('cart.title')}
        </h1>

        {cart.items.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-6">{t('cart.empty')}</p>
            <Link to="/">
              <Button className="bg-mutagim-red hover:bg-red-700">
                <ArrowIcon className="h-4 w-4 me-2" />
                {t('cart.continueShopping')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <div 
                  key={`${item.product_id}-${item.variant_id || ''}`}
                  className="flex gap-4 bg-gray-900 rounded-lg p-4 border border-gray-800"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Image - Cliquable vers le produit */}
                  {/* Utiliser l'image de la variante si disponible, sinon l'image par défaut */}
                  <Link 
                    to={`/product/${item.product?.slug || item.product_id}`}
                    className="w-24 h-24 flex-shrink-0 bg-gray-800 rounded-lg overflow-hidden hover:ring-2 hover:ring-mutagim-red transition-all"
                  >
                    <img
                      src={getImageUrl(item.metadata?.variant_image || item.product?.image)}
                      alt={item.product?.name?.[i18n.language] || ''}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    {/* Nom cliquable vers le produit */}
                    <Link 
                      to={`/product/${item.product?.slug || item.product_id}`}
                      className="text-white font-medium truncate block hover:text-mutagim-red transition-colors"
                    >
                      {item.product?.name?.[i18n.language] || item.product?.name?.fr || 'Produit'}
                    </Link>
                    
                    {/* Variante / Couleur */}
                    {item.metadata?.color && (
                      <p className="text-purple-400 text-sm mt-1">
                        <span className="text-gray-500">{t('product.color', 'Couleur')}:</span> {item.metadata.color}
                      </p>
                    )}
                    
                    {item.product?.variant_attributes && Object.keys(item.product.variant_attributes).length > 0 && (
                      <p className="text-gray-400 text-sm mt-1">
                        {Object.entries(item.product.variant_attributes).map(([key, value]) => (
                          <span key={key}>{key}: {value} </span>
                        ))}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-4">
                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 border-gray-700 text-white hover:bg-gray-800"
                          onClick={() => handleQuantityChange(index, -1)}
                          disabled={loading}
                          data-testid={`decrease-qty-${index}`}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="text-white w-8 text-center">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 border-gray-700 text-white hover:bg-gray-800"
                          onClick={() => handleQuantityChange(index, 1)}
                          disabled={loading}
                          data-testid={`increase-qty-${index}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Price */}
                      <div className="text-end">
                        <p className="text-mutagim-red font-bold">
                          {formatPrice(item.line_total)}
                        </p>
                        <p className="text-gray-500 text-sm">
                          {formatPrice(item.product?.prices)} / {t('product.quantity').toLowerCase()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-400 hover:text-mutagim-red"
                    onClick={() => handleRemove(index)}
                    data-testid={`remove-item-${index}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-900 rounded-lg p-6 border border-gray-800 sticky top-24">
                <h2 className="text-xl font-bold text-white mb-4">
                  {t('checkout.orderSummary')}
                </h2>

                {/* Promo code */}
                <div className="flex gap-2 mb-6">
                  <Input
                    placeholder={t('cart.promoCode')}
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white"
                    data-testid="promo-input"
                  />
                  <Button 
                    variant="outline" 
                    className="border-gray-700 text-white hover:bg-gray-800"
                    data-testid="apply-promo"
                  >
                    {t('cart.applyPromo')}
                  </Button>
                </div>

                <div className="space-y-3 border-t border-gray-800 pt-4">
                  <div className="flex justify-between text-gray-400">
                    <span>{t('cart.subtotal')}</span>
                    <span>{formatPrice(cart.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>{t('cart.shipping')}</span>
                    <span className="text-green-500">Gratuit</span>
                  </div>
                  {cart.promo_discount_amount > 0 && (
                    <div className="flex justify-between text-green-500">
                      <span>Réduction</span>
                      <span>-{formatPrice({ [currency]: cart.promo_discount_amount })}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-white font-bold text-lg border-t border-gray-800 pt-3">
                    <span>{t('cart.total')}</span>
                    <span className="text-mutagim-red">{formatPrice(cart.subtotal)}</span>
                  </div>
                </div>

                <Button 
                  className="w-full mt-6 bg-mutagim-red hover:bg-red-700 text-white font-semibold py-6"
                  onClick={() => navigate('/checkout')}
                  data-testid="checkout-btn"
                >
                  {t('cart.checkout')}
                </Button>

                <Link to="/" className="block mt-4">
                  <Button variant="ghost" className="w-full text-gray-400 hover:text-white">
                    <ArrowIcon className="h-4 w-4 me-2" />
                    {t('cart.continueShopping')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}

export default CartPage;
